import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpInterceptor,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ToastService } from '../services/toast.service';
import { WalletService } from '../services/walletService.service';

/**
 * Surfaces the backend's per-action coin billing on the 16 recruitment
 * endpoints (JD refine, job/user approve-reject, JD create/edit, exam
 * creation, CV ranking, approve+notify, hiring report, interview
 * scheduling, bulk/walk-in CV upload, external upload link, exam invite,
 * offer letter, onboarding):
 *   - a successful response on one of those endpoints now carries a
 *     `coin_consumption` object — shown as a toast here.
 *   - insufficient coins comes back as HTTP 402, a duplicate in-flight
 *     request as HTTP 409 — both shown as a toast here too.
 *
 * This runs globally (registered alongside AuthInterceptor in
 * app.module.ts) rather than being wired into each call site individually,
 * so every current — and any future — billed endpoint is covered
 * automatically. It only reacts when the response actually carries
 * `coin_consumption` or the request actually failed with 402/409, so every
 * other request in the app (including candidates, who aren't billed at
 * all) passes through unchanged. Errors are always re-thrown afterwards so
 * each component's own `error:` callback still runs as before (resetting
 * loading spinners, etc.).
 */
@Injectable()
export class CoinBillingInterceptor implements HttpInterceptor {
  constructor(
    private toast: ToastService,
    private walletService: WalletService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          const consumption = event.body?.coin_consumption;
          if (consumption) {
            this.toast.showCoinConsumption(consumption);
            // Keep the header's coin balance (WalletService, /wallets/wallet/)
            // in sync with what the backend just deducted, even at call
            // sites that don't already refresh it themselves.
            this.walletService.loadWallet();
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 402) {
          this.toast.showError(
            error.error?.message ||
              'Insufficient coins to perform this action.',
          );
        } else if (error.status === 409) {
          this.toast.showWarn(
            error.error?.message ||
              'This action is already being processed. Please wait a moment and try again.',
          );
        }

        return throwError(() => error);
      }),
    );
  }
}
